import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getLanguages,
  saveTranslation,
} from "../services/translationCrudService";

export default function TranslationKeyModal({
  open,
  setOpen,
  translationKey,
  mode,
  existingTranslations,
  onCreated,
}) {
  const { token } = useSelector((state) => state.user);
  const [languages, setLanguages] = useState([]);
  const [values, setValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      const response = await getLanguages(
        `${process.env.REACT_APP_HOST_BASE_URL}/v1/language`,
        token,
      );
      if (response.success) {
        setLanguages(response.data.filter((item) => item.active));
      }

      if (mode === "view") {
        const initial = {};
        (existingTranslations || []).forEach((item) => {
          initial[item.code] = item.value || "";
        });
        setValues(initial);
      } else {
        setValues({});
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, translationKey, mode]);

  const handleValueChange = (code, value) => {
    setValues((prev) => ({ ...prev, [code]: value }));
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    setIsSaving(true);
    const translationsPayload = languages.map((lang) => ({
      code: lang.code,
      value: values[lang.code] || "",
    }));
    const response = await saveTranslation(
      `${process.env.REACT_APP_HOST_BASE_URL}/v1/translation`,
      token,
      null,
      { key: translationKey, translations: translationsPayload },
    );
    setIsSaving(false);
    if (response.success) {
      toast.success("Muvaffaqiyatli saqlandi", {
        position: "bottom-right",
        autoClose: 3000,
        closeOnClick: true,
        className: "add-success",
      });
      onCreated?.(translationKey);
      setOpen(false);
    } else {
      toast.error("Xatolik yuz berdi", {
        position: "bottom-right",
        autoClose: 5000,
        closeOnClick: true,
      });
    }
  };

  const isView = mode === "view";

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-full p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full text-left transition-all transform bg-white rounded-xl shadow-2xl sm:my-8 sm:w-full sm:max-w-lg overflow-hidden">
                <form onSubmit={handleSubmit}>
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      {isView ? "Tarjimani ko'rish" : "Yangi tarjima yaratish"}
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="text-gray-400 rounded-md hover:text-gray-500 focus:outline-none"
                    >
                      <span className="sr-only">Yopish</span>
                      <XMarkIcon className="w-6 h-6" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="px-6 py-5 space-y-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Kalit
                      </label>
                      <input
                        type="text"
                        value={translationKey}
                        disabled
                        className="block w-full border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-500 sm:text-sm"
                      />
                    </div>
                    {languages.map((lang) => (
                      <div key={lang.code}>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          {lang.name}
                        </label>
                        <input
                          type="text"
                          disabled={isView}
                          value={values[lang.code] || ""}
                          onChange={(e) =>
                            handleValueChange(lang.code, e.target.value)
                          }
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                    <button
                      type="button"
                      className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => setOpen(false)}
                    >
                      {isView ? "Yopish" : "Bekor qilish"}
                    </button>
                    {!isView && (
                      <button
                        type="submit"
                        disabled={isSaving}
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60"
                      >
                        Saqlash
                      </button>
                    )}
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
